import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useAuthContext } from 'contexts/AuthContext';
import Modal from './Modal';
import FilterablePoolSelect from './FilterablePoolSelect';
import FilterableCompanySelect from './FilterableCompanySelect';
import { httpGetPumpDetails } from 'services/pumps';
import { getApiErrorMessage } from 'utils/apiErrors';
import { buildPumpSpecificationFields } from 'utils/pumpSpecs';
import { getSettablePumpStatusCodes } from './pumpStatusMeta';
import './EditPumpModal.scss';

export default function EditPumpModal({ isOpen, pumpId, initialData, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { updatePump: updatePumpInStore } = useStoreActions((actions) => actions.pumps);
  const { pools } = useStoreState((state) => state.pools);
  const { companies } = useStoreState((state) => state.companies);
  const { getCompanies } = useStoreActions((actions) => actions.companies);
  const { userRole, myUser } = useAuthContext();
  // Status as loaded from the server, so the options don't change while the user edits.
  const [loadedStatus, setLoadedStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    serial_number: '',
    model: '',
    lift_height: '',
    volume_capacity: '',
    current_pool_id: '',
    company_id: '',
    status: 2,
    spyro_round_policy: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isKorjaaja = userRole === 3;
  const isKaivosKayttaja = userRole === 4;
  const limitedPumpFields = isKorjaaja;
  const inUseReadOnlyForRepairer = isKorjaaja && Number(formData.status) === 2;
  const isAdmin = Number(userRole) === 1;
  const profileCompanyId = useMemo(() => {
    const c = myUser?.company;
    if (c == null || c === '') return null;
    const n = Number(c);
    return Number.isFinite(n) ? n : null;
  }, [myUser?.company]);
  const companySelectionLocked = !isAdmin;
  const lockedCompanyDisplayName =
    myUser?.company_name ||
    companies.find((c) => c.id === profileCompanyId)?.name ||
    '';

  useEffect(() => {
    if (!isOpen || !pumpId) return;

    if (isAdmin && companies.length === 0) {
      getCompanies();
    }

    const resolveCompanyIdForForm = (rawId) => {
      if (companySelectionLocked && profileCompanyId != null) {
        return profileCompanyId;
      }
      return rawId;
    };

    const fetchData = async () => {
      try {
        if (initialData) {
          setLoading(false);
          const companyId =
            typeof initialData.company === 'object'
              ? initialData.company?.id
              : initialData.company || initialData.company_id || '';
          setFormData({
            name: initialData.name || '',
            serial_number: initialData.serial_number || '',
            model: initialData.model || '',
            lift_height: initialData.lift_height ?? '',
            volume_capacity: initialData.volume_capacity ?? '',
            current_pool_id: initialData.current_pool?.id || '',
            company_id: resolveCompanyIdForForm(companyId),
            status: initialData.status || 2,
            spyro_round_policy: !!initialData.spyro_round_policy,
          });
          setLoadedStatus(initialData.status ?? null);
        } else {
          setLoading(true);
          const pumpRes = await httpGetPumpDetails(pumpId);
          const pump = pumpRes.data;
          const companyId =
            typeof pump.company === 'object'
              ? pump.company?.id
              : pump.company || pump.company_id || '';
          setFormData({
            name: pump.name || '',
            serial_number: pump.serial_number || '',
            model: pump.model || '',
            lift_height: pump.lift_height ?? '',
            volume_capacity: pump.volume_capacity ?? '',
            current_pool_id: pump.current_pool?.id || '',
            company_id: resolveCompanyIdForForm(companyId),
            status: pump.status || 2,
            spyro_round_policy: !!pump.spyro_round_policy,
          });
          setLoadedStatus(pump.status ?? null);
          setLoading(false);
        }

        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(t('pump_modal_load_error'));
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only for error strings; omit to avoid refetch on language change
  }, [
    isOpen,
    pumpId,
    initialData,
    companies.length,
    getCompanies,
    isAdmin,
    companySelectionLocked,
    profileCompanyId,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (companySelectionLocked) {
        if (profileCompanyId == null) {
          setError(t('pump_modal_company_missing_profile'));
          setSaving(false);
          return;
        }
      }

      const companyForApi = isAdmin
        ? formData.company_id === ''
          ? null
          : parseInt(formData.company_id, 10)
        : profileCompanyId;

      const submitData = {
        ...formData,
        current_pool_id: formData.current_pool_id === '' ? null : parseInt(formData.current_pool_id, 10),
        company: companyForApi,
        ...buildPumpSpecificationFields({
          lift_height: formData.lift_height,
          volume_capacity: formData.volume_capacity,
        }),
      };
      // Omit capacity so legacy % values are not cleared by web edits
      delete submitData.company_id;

      const updatedPump = await updatePumpInStore({ id: pumpId, data: submitData });

      if (updatedPump) {
        if (onSuccess) {
          onSuccess(updatedPump);
        }
        onClose();
      } else {
        setError(t('pump_modal_update_error'));
      }
    } catch (err) {
      console.error('Error updating pump:', err);
      setError(getApiErrorMessage(err, t('pump_modal_update_error')));
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = useMemo(() => {
    const codes = getSettablePumpStatusCodes(userRole, loadedStatus);
    return codes.map((value) => ({ value, label: t(`pump_status_${value}`) }));
  }, [userRole, loadedStatus, t]);

  // Fallback option for showing current status when the user is not allowed to set it.
  const currentStatusFallback = useMemo(() => {
    const code = Number(formData.status);
    if (!Number.isFinite(code)) return null;
    const settable = getSettablePumpStatusCodes(userRole, loadedStatus);
    return settable.includes(code) ? null : code;
  }, [formData.status, userRole, loadedStatus]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('pump_modal_edit_title')} size="medium">
      {loading ? (
        <div className="text-center py-5">{t('ui_loading')}</div>
      ) : (
        <form className="edit-modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('pump_modal_company')}</label>
            {companySelectionLocked ? (
              <input
                type="text"
                className="form-input"
                readOnly
                value={lockedCompanyDisplayName || t('pump_detail_unknown')}
                aria-label={t('pump_modal_company')}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  cursor: 'default',
                }}
              />
            ) : (
              <FilterableCompanySelect
                companies={companies}
                value={formData.company_id}
                onChange={(companyId) => handleInputChange('company_id', companyId)}
                placeholder={t('pump_modal_company_ph')}
                emptyOptionLabel={t('pump_modal_no_company')}
                disabled={limitedPumpFields}
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_name')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('pump_modal_name_ph')}
              required
              disabled={limitedPumpFields || isKaivosKayttaja}
              style={
                limitedPumpFields || isKaivosKayttaja
                  ? {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    }
                  : {}
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_serial')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.serial_number}
              onChange={(e) => handleInputChange('serial_number', e.target.value)}
              placeholder="SN123456"
              required
              disabled={limitedPumpFields || isKaivosKayttaja}
              style={
                limitedPumpFields || isKaivosKayttaja
                  ? {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    }
                  : {}
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_model')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              placeholder={t('pump_modal_model_ph')}
              disabled={limitedPumpFields || isKaivosKayttaja}
              style={
                limitedPumpFields || isKaivosKayttaja
                  ? {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed',
                    }
                  : {}
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_lift_height')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.lift_height}
              onChange={(e) => handleInputChange('lift_height', e.target.value)}
              placeholder={t('pump_modal_lift_height_ph')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_volume_capacity')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.volume_capacity}
              onChange={(e) => handleInputChange('volume_capacity', e.target.value)}
              placeholder={t('pump_modal_volume_capacity_ph')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_pool')}</label>
            <FilterablePoolSelect
              pools={pools}
              value={formData.current_pool_id}
              onChange={(poolId) => handleInputChange('current_pool_id', poolId)}
              placeholder={t('filter_pool_default_ph')}
              emptyOptionLabel={t('pump_modal_pool_empty')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pump_modal_status')}</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => handleInputChange('status', parseInt(e.target.value, 10))}
              required
              disabled={inUseReadOnlyForRepairer}
            >
              {currentStatusFallback != null && (
                <option value={currentStatusFallback} disabled>
                  {t(`pump_status_${currentStatusFallback}`)}
                  {t('pump_detail_status_current_suffix')}
                </option>
              )}
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={!!formData.spyro_round_policy}
                onChange={(e) => handleInputChange('spyro_round_policy', e.target.checked)}
                disabled={limitedPumpFields || isKaivosKayttaja}
              />
              {t('pump_modal_spyro')}
            </label>
            <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '4px' }}>
              {t('pump_modal_spyro_help')}
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                saving ||
                inUseReadOnlyForRepairer ||
                !formData.name ||
                !formData.serial_number ||
                (companySelectionLocked && profileCompanyId == null)
              }
            >
              {saving ? t('ui_saving') : t('Save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
